import { watch } from 'vue'
import { useCookieConsent } from '~/composables/useCookieConsent'

export default defineNuxtPlugin(() => {
  if (typeof window === 'undefined') {
    return
  }

  const installCmpkitConsoleFilter = () => {
    const consoleState = window as typeof window & {
      __kendrickCmpkitConsoleFilterInstalled?: boolean
    }

    if (consoleState.__kendrickCmpkitConsoleFilterInstalled) return

    consoleState.__kendrickCmpkitConsoleFilterInstalled = true

    const originalLog = console.log.bind(console)
    console.log = (...args: unknown[]) => {
      if (args.length === 1 && args[0] === '[CMPKit]') return
      originalLog(...args)
    }
  }

  installCmpkitConsoleFilter()

  let cmpkitScript: HTMLScriptElement | null = null
  let cmpkitLoadRequested = false
  let consentPollId: number | null = null
  let consentSyncTimeoutId: number | null = null
  let focusListenerAttached = false
  let currentCmpkitLocale: 'en' | 'fr' | 'es' = 'en'
  let cmpkitCopyObserver: MutationObserver | null = null
  const { updateConsent } = useCookieConsent()

  const cmpkitCopyOverrides = {
    fr: {
      'Cookie Preferences': 'Préférences cookies',
      'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.': 'Nous utilisons des cookies pour améliorer votre expérience, proposer du contenu personnalisé et analyser notre trafic.',
      'Consent by CMPKit': 'Consentement par CMPKit'
    },
    es: {
      'Cookie Preferences': 'Preferencias de cookies',
      'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.': 'Usamos cookies para mejorar tu experiencia de navegación, ofrecer contenido personalizado y analizar nuestro tráfico.',
      'Consent by CMPKit': 'Consentimiento por CMPKit'
    }
  } satisfies Record<'fr' | 'es', Record<string, string>>

  const isCrawlerUserAgent = () => {
    const ua = navigator.userAgent || ''
    return /Googlebot|Google-InspectionTool|AdsBot-Google|APIs-Google|Mediapartners-Google|bingbot|DuckDuckBot|Slurp|YandexBot|facebookexternalhit|Twitterbot|LinkedInBot|Pinterestbot/i.test(ua)
  }

  const shouldLoadCmpkit = (path: string): boolean => {
    if (isCrawlerUserAgent()) return false

    // Don't load CMPKit on /app/* routes
    const isAppRoute = path.startsWith('/app/') || path.startsWith('/fr/app/') || path === '/app' || path === '/fr/app'
    return !isAppRoute
  }

  const getCmpkitLocale = (path: string): 'en' | 'fr' | 'es' => {
    if (path === '/fr' || path.startsWith('/fr/')) return 'fr'
    if (path === '/es' || path.startsWith('/es/')) return 'es'
    return 'en'
  }

  const getCmpkitAccessibleTitle = (locale: 'en' | 'fr' | 'es') => {
    if (locale === 'fr') return 'Paramètres des cookies'
    if (locale === 'es') return 'Configuración de cookies'
    return 'Cookie settings'
  }

  // Function to sync CMPKit consent with our composable
  const syncCMPKitConsent = () => {
    if (typeof window === 'undefined') return

    const cmpkit = (window as any).cmpkit
    if (!cmpkit) {
      return
    }

    try {
      const consent = cmpkit.getConsent()
      if (consent) {
        updateConsent({
          necessary: consent.necessary !== false, // Default to true
          analytics: consent.analytics === true,
          marketing: consent.marketing === true,
          preferences: consent.preferences === true || consent.functional === true
        })
      }
    } catch (error) {
      console.warn('[CMPKit] Error syncing consent:', error)
    }
  }

  const checkConsent = () => {
    syncCMPKitConsent()
  }

  const stopConsentSync = () => {
    if (consentSyncTimeoutId !== null) {
      window.clearTimeout(consentSyncTimeoutId)
      consentSyncTimeoutId = null
    }

    if (consentPollId !== null) {
      window.clearInterval(consentPollId)
      consentPollId = null
    }

    if (focusListenerAttached) {
      window.removeEventListener('focus', checkConsent)
      focusListenerAttached = false
    }
  }

  const stopCmpkitCopyLocalization = () => {
    cmpkitCopyObserver?.disconnect()
    cmpkitCopyObserver = null
  }

  const localizeCmpkitCopy = (root: Node = document.body) => {
    if (currentCmpkitLocale === 'en') return

    const overrides = cmpkitCopyOverrides[currentCmpkitLocale]
    if (!overrides) return

    const visit = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const original = node.textContent?.trim() || ''
        const replacement = overrides[original]
        if (replacement && node.textContent) {
          node.textContent = node.textContent.replace(original, replacement)
        }
        return
      }

      node.childNodes.forEach(visit)
    }

    visit(root)
  }

  const startCmpkitCopyLocalization = (delay = 0) => {
    stopCmpkitCopyLocalization()

    if (currentCmpkitLocale === 'en') return

    const boot = () => {
      localizeCmpkitCopy()
      cmpkitCopyObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'characterData') {
            localizeCmpkitCopy(mutation.target)
            return
          }

          mutation.addedNodes.forEach(node => localizeCmpkitCopy(node))
        })
      })
      cmpkitCopyObserver.observe(document.body, {
        childList: true,
        characterData: true,
        subtree: true
      })
    }

    if (delay > 0) {
      window.setTimeout(boot, delay)
      return
    }

    boot()
  }

  const startConsentSync = (delay = 0) => {
    stopConsentSync()

    const boot = () => {
      syncCMPKitConsent()

      if (consentPollId === null) {
        consentPollId = window.setInterval(checkConsent, 1000)
      }

      if (!focusListenerAttached) {
        window.addEventListener('focus', checkConsent)
        focusListenerAttached = true
      }
    }

    if (delay > 0) {
      consentSyncTimeoutId = window.setTimeout(() => {
        consentSyncTimeoutId = null
        boot()
      }, delay)
      return
    }

    boot()
  }

  const loadCmpkit = (path: string) => {
    if (cmpkitLoadRequested) return
    cmpkitLoadRequested = true
    currentCmpkitLocale = getCmpkitLocale(path)

    const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://cmpkit.com/widget.js"]')
    if (existingScript) {
      cmpkitScript = existingScript
      existingScript.setAttribute('data-locale', currentCmpkitLocale)
      existingScript.setAttribute('data-accessible-title', getCmpkitAccessibleTitle(currentCmpkitLocale))
      startConsentSync(100)
      startCmpkitCopyLocalization(150)
      return
    }

    const script = document.createElement('script')
    script.src = 'https://cmpkit.com/widget.js'
    script.setAttribute('data-api-key', 'cmpkit_8425cddf1bdb7ca04e5c1c310b95c7a75e3bd06e9b4b260180a8285ef4fe06c2')
    script.setAttribute('data-datalayer', 'true')
    script.setAttribute('data-datalayer-forward-all', 'false')
    script.setAttribute('data-auto-inject-vendors', 'true')
    script.setAttribute('data-locale', currentCmpkitLocale)
    script.setAttribute('data-accessible-title', getCmpkitAccessibleTitle(currentCmpkitLocale))
    script.defer = true
    cmpkitScript = script

    script.onload = () => {
      startConsentSync(500)
      startCmpkitCopyLocalization(700)
    }

    script.onerror = () => {
      console.error('[CMPKit] Failed to load script')
      stopConsentSync()
      cmpkitLoadRequested = false
    }

    document.head.appendChild(script)
  }

  const removeCmpkit = () => {
    stopConsentSync()
    stopCmpkitCopyLocalization()

    try {
      ;(window as any).cmpkit?.destroy?.()
    } catch (error) {
      console.warn('[CMPKit] Error destroying widget:', error)
    }

    if (cmpkitScript && cmpkitScript.parentNode) {
      cmpkitScript.parentNode.removeChild(cmpkitScript)
      cmpkitScript = null
    } else {
      // Fallback: try to find and remove by src
      const existingScript = document.querySelector('script[src="https://cmpkit.com/widget.js"]')
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript)
      }
    }
    cmpkitLoadRequested = false
  }

  const route = useRoute()
  const path = route?.path || window.location.pathname

  if (shouldLoadCmpkit(path)) {
    loadCmpkit(path)
  }

  watch(() => route.path, (newPath) => {
    if (shouldLoadCmpkit(newPath)) {
      if (!cmpkitScript || !document.querySelector('script[src="https://cmpkit.com/widget.js"]') || currentCmpkitLocale !== getCmpkitLocale(newPath)) {
        removeCmpkit()
        loadCmpkit(newPath)
      }
    } else {
      removeCmpkit()
    }
  })
})
